import {
  AudienceIcon,
  Badge,
  type BadgeProps,
  DbtIcon,
  JourneyIcon,
  ModelIcon,
  SqlIcon,
  TableIcon,
} from "@hightouch/htui";
import omit from "lodash/omit";
import uniqBy from "lodash/uniqBy";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import type { CustomQuery } from "src/components/sources/forms/custom-query";
import { useUser } from "src/contexts/user-context";
import {
  type BackgroundJobResult,
  type QueryResponse,
  type RunCustomBackgroundQuery,
  type RunCustomBackgroundQueryVariables,
  type RunDbtModelBackgroundQuery,
  type RunDbtModelBackgroundQueryVariables,
  type RunDecisionEngineBackgroundQuery,
  type RunDecisionEngineBackgroundQueryVariables,
  type RunJourneyNodeBackgroundQuery,
  type RunJourneyNodeBackgroundQueryVariables,
  type RunLcsTriggeredSendBackgroundQueryVariables,
  type RunLookerLookBackgroundQuery,
  type RunLookerLookBackgroundQueryVariables,
  type RunSigmaBackgroundQuery,
  type RunSigmaBackgroundQueryVariables,
  type RunSqlBackgroundQuery,
  type RunSqlBackgroundQueryVariables,
  type RunTableBackgroundQuery,
  type RunTableBackgroundQueryVariables,
  type RunVisualBackgroundQuery,
  type RunVisualBackgroundQueryVariables,
  type SaveModelConfigurationInput,
  type SegmentsInsertInput,
  type SegmentsSetInput,
  type SuccessfulQueryResponse,
  type UpdateAudienceSubsetsMutationVariables,
  useCancelPreviewQueryJobMutation,
  useRunCustomBackgroundQuery,
  useRunDbtModelBackgroundQuery,
  useRunDecisionEngineBackgroundQuery,
  useRunJourneyNodeBackgroundQuery,
  useRunLcsTriggeredSendBackgroundQuery,
  useRunLookerLookBackgroundQuery,
  useRunSigmaBackgroundQuery,
  useRunSqlBackgroundQuery,
  useRunSqlResultQuery,
  useRunModelPreviewSqlResultQuery,
  useRunTableBackgroundQuery,
  useVisualQueryBackgroundResultQuery,
} from "src/graphql";
import type {
  FilterableColumnFragment,
  QueryColumn,
  Scalars,
} from "src/graphql/types";
import { useRunVisualBackgroundQuery } from "src/graphql";
import { usePreviewQuerySchema } from "src/hooks/use-preview-query-schema";
import { captureException, isReactQueryCancellation } from "src/lib/monitoring";
import { QueryType, type Sigma } from "src/types/models";
import {
  isColumnPreviewMasked,
  REDACTED_PREVIEW_VALUE,
} from "src/utils/preview-redaction";
import {
  type AndCondition,
  ColumnType,
  ConditionType,
  type RootCondition,
  type VisualQueryFilter,
} from "src/types/visual";
import type { MarkOptional } from "ts-essentials";
import { isPresent } from "ts-extras";
import { v4 as uuid } from "uuid";

export const QueryTypeDictionary: Record<QueryType, string> = {
  [QueryType.RawSql]: "SQL",
  [QueryType.Visual]: "Visual",
  [QueryType.Table]: "Table",
  [QueryType.DbtModel]: "dbt Model",
  [QueryType.DbtCloudModel]: "dbt Cloud Model",
  [QueryType.LookerLook]: "Looker Look",
  [QueryType.Sigma]: "Sigma",
  [QueryType.Custom]: "Custom",
  [QueryType.JourneyNode]: "Journey",
  [QueryType.DecisionEngine]: "Decision Engine",
  [QueryType.IDRGoldenRecordParent]: "Golden Record Parent",
  [QueryType.IDRGoldenRecord]: "Golden Record",
  [QueryType.Predictive]: "Predictive",
  [QueryType.CepManagedModel]: "Native Delivery Managed Model",
  [QueryType.RealtimeTraitWarehouseState]: "Realtime Trait Warehouse State",
  [QueryType.LcsTriggeredSend]: "Lifecycle Studio Triggered Send",
  [QueryType.CepSuppressionList]: "Native Delivery suppression list",
};

export const ASYNC_RUN_QUERY = {
  [QueryType.RawSql]: useRunSqlBackgroundQuery,
  [QueryType.Visual]: useRunVisualBackgroundQuery,
  [QueryType.Table]: useRunTableBackgroundQuery,
  [QueryType.DbtModel]: useRunDbtModelBackgroundQuery,
  [QueryType.DbtCloudModel]: useRunDbtModelBackgroundQuery,
  [QueryType.LookerLook]: useRunLookerLookBackgroundQuery,
  [QueryType.Sigma]: useRunSigmaBackgroundQuery,
  [QueryType.Custom]: useRunCustomBackgroundQuery,
  [QueryType.JourneyNode]: useRunJourneyNodeBackgroundQuery,
  [QueryType.DecisionEngine]: useRunDecisionEngineBackgroundQuery,
  [QueryType.LcsTriggeredSend]: useRunLcsTriggeredSendBackgroundQuery,
};

// Error type used to signal that a column deletion error occurred, so the frontend
// can handle it specially.
export class DeleteColumnsError extends Error {
  deletedColumns: string[];

  constructor(
    message: string,
    options: ErrorOptions & { deletedColumns: string[] },
  ) {
    super(message, options);
    this.deletedColumns = options.deletedColumns;
  }
}

export const EMPTY_AUDIENCE_DEFINITION: VisualQueryFilter = { conditions: [] };

export const EMPTY_CONDITION: AndCondition<RootCondition> = {
  type: ConditionType.And,
  conditions: [],
};

function getVisualQueryFilter(
  filter?: VisualQueryFilter | null,
  options?: ModelRunOptions,
): Scalars["JSONObject"] {
  if (!filter) {
    return EMPTY_AUDIENCE_DEFINITION;
  }

  if (options?.includeMergedColumns) {
    return {
      ...filter,
      additionalColumns: uniqBy(
        [
          ...(filter.additionalColumns ?? []),
          ...(options.mergedColumns ?? []).map((col) => ({
            alias: `${col.model_name}:${col.name}`,
            column: col.column_reference,
          })),
        ],
        "alias",
      ),
    };
  }

  return filter;
}

export const getVariables = (model: ModelState, options?: ModelRunOptions) => {
  const type = model.query_type as QueryType;
  switch (type) {
    case QueryType.RawSql: {
      const variables: RunSqlBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        sql: model.query_raw_sql ?? "",
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.Visual: {
      const filter = getVisualQueryFilter(model.visual_query_filter, options);

      const variables: RunVisualBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        parentModelId:
          model.visual_query_parent_id?.toString() ??
          model.parent?.id?.toString() ??
          "",
        filter,
        audienceId: model.id?.toString(),
        subsetIds:
          options?.useDefaultSubsets || !model.subsets?.length
            ? undefined
            : model.subsets.map((subset) => subset.subset_value.id.toString()),
        relatedModelId: options?.relatedModelId?.toString(),
        useDefaultSubsets: options?.useDefaultSubsets,
        useSampledModels: options?.useSampledModels,
        includeColumnsFromFilter: options?.includeColumnsFromFilter,
        includeTraitDependencies: options?.includeTraitDependencies,
        destinationId: options?.destinationId?.toString(),
        syncConfig: options?.syncConfig,
        primaryKeyIds: options?.primaryKeyIds,
      };
      return variables;
    }
    case QueryType.Table: {
      const variables: RunTableBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        table: model.query_table_name ?? "",
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.DbtCloudModel:
    case QueryType.DbtModel: {
      const variables: RunDbtModelBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        dbtModelId: Number(model.query_dbt_model_id),
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.LookerLook: {
      const variables: RunLookerLookBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        lookId: model.query_looker_look_id ?? "",
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.Sigma: {
      const variables: RunSigmaBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        elementId: model.query_integrations?.query?.elementId ?? "",
        workbookId: model.query_integrations?.query?.workbookId ?? "",
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.Custom: {
      const variables: RunCustomBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        customQuery: model.custom_query,
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.JourneyNode: {
      const variables: RunJourneyNodeBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.LcsTriggeredSend: {
      const variables: RunLcsTriggeredSendBackgroundQueryVariables = {
        sourceId: String(model.connection?.id),
        modelId: Number(model.id),
      };
      return variables;
    }
    case QueryType.DecisionEngine: {
      const variables: RunDecisionEngineBackgroundQueryVariables = {
        sqlOverride: model.query_raw_sql,
        sourceId: String(model.connection?.id),
        modelId: Number(model.id),
      };
      return variables;
    }
    default:
      throw new Error(`Unsupported query type ${type}`);
  }
};

type InnerBackgroundQueryResult =
  | RunVisualBackgroundQuery
  | RunSqlBackgroundQuery
  | RunTableBackgroundQuery
  | RunDbtModelBackgroundQuery
  | RunLookerLookBackgroundQuery
  | RunSigmaBackgroundQuery
  | RunCustomBackgroundQuery
  | RunDecisionEngineBackgroundQuery
  | RunJourneyNodeBackgroundQuery;

export function getBackgroundInnerQueryResult(
  result: InnerBackgroundQueryResult | null | undefined,
): BackgroundJobResult {
  if (!result) {
    // Can happen if the server returns a response with no `data` and no
    // `errors` (e.g. an intermediary returning an empty body) — surface a
    // clear message instead of the opaque TypeError from Object.values(null).
    throw new Error("Query run returned an empty response from the server");
  }
  return Object.values(result)[0] as BackgroundJobResult;
}

export type ModelState = {
  query_type: string | null;
  id?: string | number;
  connection?: { id: string | number | null } | null;
  primary_key?: string | null;
  visual_query_parent_id?: string | number | null;
  event?: {
    realtime_event_type: string | null;
    timestamp_column: string | null;
  } | null;
  parent?: {
    id: string | number | null;
  } | null;
  visual_query_filter?: VisualQueryFilter | null;
  visual_query_primary_label?: string | null;
  visual_query_secondary_label?: string | null;
  query_dbt_model_id?: number | null;
  query_looker_look_id?: string | null;
  query_table_name?: string | null;
  query_raw_sql?: string | null;
  query_integrations?: Sigma | null;
  custom_query?: CustomQuery | null;
  subsets?: Array<{ subset_value: { id: string } }>;
  is_streamable?: boolean | null;
  columns?: Array<{
    name: string;
    alias: string | null;
    disable_preview: boolean;
    redacted_text: string | null;
    privacy_level?: string | null;
    type: string | null;
    raw_type: string | null;
    custom_type: string | null;
    metadata?: { properties: any } | null;
  }>;
  matchboosting_enabled?: boolean | null;
  audience_templates?: Array<{ id: string }>;
  realtime_enabled?: boolean | null;
};

/** Flattens a model's subset selections into the subset-value id list that the
 * subset pickers and preview queries consume. */
export const getSubsetIds = (subsets: ModelState["subsets"]): string[] =>
  subsets?.map((subset) => subset.subset_value.id) ?? [];

/** Inverse of {@link getSubsetIds}: shapes a subset-value id list back into the
 * `subsets` form `modelState.onChange` expects. */
export const subsetsFromIds = (ids: string[]): ModelState["subsets"] =>
  ids.map((id) => ({ subset_value: { id } }));

/**
 * Order-insensitive comparison of two subset id lists as sets. The subset
 * selector can reorder ids without any user action, so ordering must never
 * count as a change.
 */
export const subsetIdsEqual = (
  a: string[] | null | undefined,
  b: string[] | null | undefined,
): boolean => {
  const setA = new Set(a ?? []);
  const setB = new Set(b ?? []);
  if (setA.size !== setB.size) {
    return false;
  }
  for (const id of setA) {
    if (!setB.has(id)) {
      return false;
    }
  }
  return true;
};

/** `UpdateAudienceSubsets` also deletes the audience's subsets missing from `subsetIds`, so an empty list clears them all. */
export function getAudienceSubsetsUpdateVariables(
  audienceId: string,
  subsetIds: string[],
): UpdateAudienceSubsetsMutationVariables {
  return {
    audience_id: audienceId,
    upsert_objects: subsetIds.map((subsetValueId) => ({
      segment_id: audienceId,
      subset_value_id: subsetValueId,
    })),
    upsert_subset_ids: subsetIds,
  };
}

export const getModelInputFromState = (
  state: ModelState,
  {
    includeSubsets,
    includeTemplates,
  }: { includeSubsets: boolean; includeTemplates: boolean } = {
    includeSubsets: true,
    includeTemplates: true,
  },
): SegmentsInsertInput => {
  const input: SegmentsInsertInput = {
    query_type: state.query_type,
    connection_id: String(state.connection?.id),
    query_raw_sql: state.query_raw_sql,
    query_table_name: state.query_table_name,
    query_dbt_model_id: state.query_dbt_model_id,
    query_looker_look_id: state.query_looker_look_id,
    query_integrations: state.query_integrations,
    custom_query: state.custom_query,
    visual_query_filter: state.visual_query_filter,
    is_streamable: Boolean(state.is_streamable),
    primary_key: state.primary_key,
    realtime_enabled: state.realtime_enabled,
  };

  if (includeSubsets) {
    input.subsets = state.subsets
      ? {
          data: state.subsets.map(({ subset_value: { id } }) => ({
            subset_value_id: String(id),
          })),
        }
      : undefined;
  }

  if (includeTemplates && state.audience_templates) {
    input.audience_templates = {
      data: state.audience_templates.map(({ id }) => ({ template_id: id })),
    };
  }

  return input;
};

/** The `_set` that saves a segment. Subsets and templates save separately, and user roles cannot update `connection_id`. */
export function getModelUpdateInputFromState(
  state: ModelState,
): SegmentsSetInput {
  return {
    ...omit(
      getModelInputFromState(state, {
        includeSubsets: false,
        includeTemplates: false,
      }),
      "connection_id",
    ),
    // The backend sets the approved draft id; clearing it here keeps saves consistent if a workspace turns approvals off again.
    approved_draft_id: null,
  };
}

/**
 * Maps local query-editing state to `saveModelConfiguration`'s input: the
 * backend validates this proposed query against the warehouse and resolves
 * the model's columns from the warehouse directly, rather than from a frontend preview.
 */
export const getSaveModelConfigurationInputFromState = (
  state: ModelState,
): SaveModelConfigurationInput => ({
  query_type: state.query_type ?? "",
  query_raw_sql: state.query_raw_sql,
  query_table_name: state.query_table_name,
  query_dbt_model_id: state.query_dbt_model_id,
  query_looker_look_id: state.query_looker_look_id,
  query_integrations: state.query_integrations,
  custom_query: state.custom_query,
  primary_key: state.primary_key,
  is_streamable: Boolean(state.is_streamable),
  realtime_enabled: state.realtime_enabled,
  visual_query_primary_label: state.visual_query_primary_label,
  visual_query_secondary_label: state.visual_query_secondary_label,
});

type ModelRunOptions = {
  /** @default false */
  disableQuery?: boolean;
  columns?: Array<{
    name: string;
    alias: string | null;
    disable_preview: boolean;
    redacted_text: string | null;
    privacy_level?: string | null;
  }>;
  destinationId?: string;
  useDefaultSubsets?: boolean;
  useSampledModels?: boolean;
  includeTraitDependencies?: boolean;
  relatedModelId?: string | null;
  onCompleted?: (
    data: {
      columns?: Omit<QueryColumn, "__typename">[];
      rows?: any[];
    },
    error?: string,
  ) => void;
  mergedColumns?: FilterableColumnFragment[];
  includeMergedColumns?: boolean;
  includeColumnsFromFilter?: boolean;
  primaryKeyIds?: string[];
  /**
   * Only honored for visual queries. When provided, the preview query is
   * enriched server-side with additional columns referenced by the sync
   * configuration (e.g. `{{ merged['Rel.COLUMN'] }}` inside a template
   * mapping body) so the "Test a row" preview reflects the same data the
   * real sync would see.
   */
  syncConfig?: Record<string, unknown>;
};

export type ModelResultData = MarkOptional<
  SuccessfulQueryResponse,
  "rows" | "numRowsWithoutLimit" | "sql" | "rowsCount"
>;

export const useModelRun = (
  model: Readonly<ModelState>,
  options?: ModelRunOptions,
) => {
  // Store the function to cancel the current query with the query's key.
  const cancelQueryRef = useRef<() => Promise<void>>();
  // Model used for the in-flight background preview (matches `modelOverride` when set).
  const modelForActiveBackgroundRunRef = useRef<Readonly<ModelState> | null>(
    null,
  );

  const client = useQueryClient();
  const { featureFlags } = useUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [backgroundJobId, setBackgroundJobId] = useState<string>();
  const [shouldPollForResults, setShouldPollForResults] =
    useState<boolean>(false);
  const [schemaLoading, setSchemaLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const [page, setPage] = useState<number | undefined>();
  const [errorAtLine, setErrorAtLine] = useState<number | undefined>();
  const [runId, setRunId] = useState<string | undefined>("");
  const [result, setResult] = useState<{
    data?: ModelResultData;
    id: string;
  }>({ id: "" });
  const [transformedSql, setTransformedSql] = useState<string | undefined>();
  const initialColumns = useMemo(
    () => options?.columns ?? model.columns ?? [],
    [options?.columns, model?.columns],
  );

  useEffect(() => {
    // If we aren't doing async pagination or the user hasn't ran the query yet.
    if (!backgroundJobId || page === undefined || backgroundJobId !== runId) {
      return;
    }

    setLoading(true);
    setShouldPollForResults(true);
  }, [page, backgroundJobId, runId]);
  const cancelBackgroundQuery = useCancelPreviewQueryJobMutation({
    onSuccess: () => {
      // set `onSuccess` as a noop so running the mutation
      // does not invalidate cache on cancellations
    },
  });
  const cancelQuery = async () => {
    await cancelQueryRef.current?.();
    modelForActiveBackgroundRunRef.current = null;
    setRunId("");
    setLoading(false);
    setSchemaLoading(false);
    setError(undefined);
    if (backgroundJobId) {
      cancelBackgroundQuery.mutate({ jobId: backgroundJobId });
    }
    setBackgroundJobId(undefined);
    setShouldPollForResults(false);
    setResult({
      ...result,
      data: undefined,
    });

    // reset cancel function.
    cancelQueryRef.current = undefined;
  };

  const modelForBackgroundPolling =
    modelForActiveBackgroundRunRef.current ?? model;

  const { getSchema } = usePreviewQuerySchema({
    sourceId: model.connection?.id?.toString(),
    queryType: model.query_type,
    getQuery: model ? () => getVariables(model, options) : undefined,
    onStart: () => {
      setSchemaLoading(true);
      const id = uuid();
      setRunId(id);
      return { runId: id };
    },
    onComplete: ({ runId, data }) => {
      setResult({ id: runId, data });
      setSchemaLoading(false);
    },
    onError: (error) => {
      setError(error.message);
      setErrorAtLine(undefined);
    },
  });

  const resetRunState = () => {
    setRunId("");
    setLoading(false);
    setSchemaLoading(false);
    setError("");
    setResult({ id: "" });
  };

  useRunSqlResultQuery(
    {
      jobId: backgroundJobId ?? "",
      page: page ?? 0,
    },
    {
      // TODO: return to using useRunModelPreviewSqlResultQuery when modelForBackgroundPolling.id exists
      //       after schema preview -> save flow is fixed for access blocked columns
      enabled: Boolean(
        shouldPollForResults &&
          backgroundJobId &&
          modelForBackgroundPolling.query_type !== QueryType.Visual,
      ),
      keepPreviousData: true,
      refetchInterval: 500,
      onError: (error) => {
        captureException(error);
        handleErrorResponse(error.message, backgroundJobId || "");
        setShouldPollForResults(false);
      },
      onSuccess: (data) => {
        if (!data?.backgroundPreviewQueryResult) {
          return;
        }

        handleSuccessResponse(
          data.backgroundPreviewQueryResult,
          backgroundJobId || "",
        );
        setShouldPollForResults(false);
      },
    },
  );

  useRunModelPreviewSqlResultQuery(
    {
      jobId: backgroundJobId ?? "",
      page: page ?? 0,
    },
    {
      // TODO: return to using useRunModelPreviewSqlResultQuery when modelForBackgroundPolling.id exists
      //       after schema preview -> save flow is fixed for access blocked columns
      enabled: false,
      keepPreviousData: true,
      refetchInterval: 500,
      onError: (error) => {
        captureException(error);
        handleErrorResponse(error.message, backgroundJobId || "");
        setShouldPollForResults(false);
      },
      onSuccess: (data) => {
        if (!data?.backgroundModelPreviewQueryResult) {
          return;
        }

        handleSuccessResponse(
          data.backgroundModelPreviewQueryResult,
          backgroundJobId || "",
        );
        setShouldPollForResults(false);
      },
    },
  );

  useVisualQueryBackgroundResultQuery(
    {
      jobId: backgroundJobId ?? "",
      page: page ?? 0,
      filter: getVisualQueryFilter(
        modelForBackgroundPolling.visual_query_filter,
        options,
      ),
      parentModelId:
        modelForBackgroundPolling.visual_query_parent_id?.toString() ??
        modelForBackgroundPolling.parent?.id?.toString() ??
        "",
    },
    {
      enabled: Boolean(
        shouldPollForResults &&
          backgroundJobId &&
          modelForBackgroundPolling.query_type === QueryType.Visual,
      ),
      refetchInterval: 500,
      onError: (error) => {
        captureException(error);
        handleErrorResponse(error.message, backgroundJobId || "");
        setShouldPollForResults(false);
      },
      onSuccess: (data) => {
        if (!data.visualQueryBackgroundResult) {
          return;
        }

        handleSuccessResponse(
          data.visualQueryBackgroundResult,
          backgroundJobId || "",
        );
        setShouldPollForResults(false);
      },
    },
  );

  const handleErrorResponse = (message: string, id: string) => {
    modelForActiveBackgroundRunRef.current = null;
    setError(message);
    setErrorAtLine(undefined);
    setLoading(false);
    setResult({ id, data: undefined });
    setTransformedSql(undefined);
  };

  const handleSuccessResponse = (responseResult: QueryResponse, id: string) => {
    setLoading(false);
    setTransformedSql(responseResult.sql || undefined);

    if (responseResult && responseResult.__typename === "FailedQueryResponse") {
      setError(responseResult.error);
      const lineNumber = responseResult.metadata?.line;
      if (lineNumber) {
        setErrorAtLine(lineNumber);
      }
      setResult({ id, data: undefined });
    } else {
      setErrorAtLine(undefined);
      setError(undefined);
      setResult({ id, data: responseResult as SuccessfulQueryResponse });
    }
    modelForActiveBackgroundRunRef.current = null;
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (result.id === runId && options?.onCompleted) {
      if (result.data) {
        options.onCompleted(
          {
            columns: result.data?.columns?.map(({ name, type, raw_type }) => ({
              name,
              type,
              raw_type,
            })), //remove __typename,
            rows: result.data?.rows,
          },
          error,
        );
      }
    }
  }, [runId, result]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const runQuery = useCallback(
    async (runOptions: {
      limit: boolean;
      disableRowCounter?: boolean;
      /**
       * Run against this snapshot instead of the hook's current `model` (e.g.
       * data returned from `queryClient.refetch()` resolves before React re-renders).
       */
      modelOverride?: Readonly<ModelState>;
    }): Promise<void> => {
      const effectiveModel = runOptions.modelOverride ?? model;

      if (!effectiveModel.query_type) {
        return undefined;
      }

      // Only run if the options are enabled
      if (options?.disableQuery) {
        return;
      }

      setLoading(true);
      const id = uuid();
      setRunId(id);
      modelForActiveBackgroundRunRef.current = effectiveModel;

      const commonVariables: Partial<RunVisualBackgroundQueryVariables> = {
        // NOTE: the feature flag names on the right side need to match the names
        // in the database
        disableRowCounter:
          featureFlags?.sql_row_counter_disabled || runOptions.disableRowCounter
            ? true
            : undefined,
        // In the backend we actually query for 101 rows, but only return 100,
        // so that we can tell if the result is truncated.
        limit: runOptions.limit ? 100 : undefined,
      };

      const variables = {
        ...commonVariables,
        ...getVariables(effectiveModel, options),
      };

      try {
        const queryKey =
          ASYNC_RUN_QUERY[effectiveModel.query_type].getKey(variables);
        const queryFn =
          effectiveModel.query_type === QueryType.Visual
            ? useRunVisualBackgroundQuery.fetcher(
                variables as RunVisualBackgroundQueryVariables,
              )
            : ASYNC_RUN_QUERY[effectiveModel.query_type].fetcher(variables);

        // set ref to cancel the current query
        cancelQueryRef.current = () => client.cancelQueries(queryKey);

        const response = await client.fetchQuery<
          InnerBackgroundQueryResult | null | undefined
        >(queryKey, {
          queryFn,
          // Disable caching the query results.
          cacheTime: 0,
        });

        // If the cancel function has been reset then the query was canceled.
        if (!cancelQueryRef.current) {
          modelForActiveBackgroundRunRef.current = null;
          return;
        }

        const responseResult = getBackgroundInnerQueryResult(response);
        setBackgroundJobId(responseResult.jobId);

        // Update the run ID here so when the response comes back, we know
        // whether it is still to the request we care about.
        setRunId(responseResult.jobId);
        setPage(0);
        setShouldPollForResults(true);
      } catch (err) {
        if (isReactQueryCancellation(err)) {
          modelForActiveBackgroundRunRef.current = null;
          return;
        }
        handleErrorResponse(err.message, id);
        captureException(err);
      }
      return;
    },
    [client, featureFlags, model, options],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const rows = useMemo(() => {
    let cols = [...initialColumns];

    if (options?.includeMergedColumns) {
      cols = cols.concat(
        (options.mergedColumns ?? []).map((col) => ({
          // Name is pre-pended with the model name
          name: `${col.model_name}:${col.name}`,
          alias: col.alias,
          disable_preview: Boolean(col.disable_preview),
          redacted_text: null,
        })),
      );
    }

    return result.data?.rows?.map((row) => aliasRow(row, cols));
  }, [result.data?.rows, initialColumns, options?.includeMergedColumns]);

  // Substitutes `model_columns.alias` into each column's `name` for preview tables only; the backend resolves saved columns from the warehouse.
  const columns = useMemo(
    () =>
      aliasColumns(result.data?.columns ?? [], initialColumns)?.map(
        (column) => ({
          name: column.name,
          type: column.type as ColumnType,
          raw_type: column.raw_type,
        }),
      ),
    [result.data?.columns, initialColumns],
  );

  const useAsyncPagination = Boolean(backgroundJobId) && page !== undefined;
  const numRowsWithoutLimit = Number(result.data?.numRowsWithoutLimit);

  return {
    runQuery,
    getSchema,
    cancelQuery,
    resetRunState,
    schemaLoading,
    loading,
    error,
    isSuccess: !error && Boolean(result.data),
    errorAtLine,
    columns,
    transformedSql,
    rows,
    numRowsWithoutLimit: Number.isNaN(numRowsWithoutLimit)
      ? null
      : numRowsWithoutLimit,
    isResultTruncated: result.data?.exceedsPreviewMax,
    asyncPagination: useAsyncPagination,
    rowsCount: isPresent(result.data?.rowsCount)
      ? Number(result.data.rowsCount)
      : null,
    page: useAsyncPagination ? page : undefined,
    setPage: useAsyncPagination ? setPage : undefined,
    usedSampledModels: result.data?.usedSampledModels,
    inferredPrimaryKey: result.data?.inferredPrimaryKey,
    roleResolution: result.data?.roleResolution ?? null,
  };
};

export const aliasColumns = <
  C extends {
    name: string;
    type: string;
    raw_type: string | null;
  },
>(
  columns: Array<C>,
  modelColumns: Array<{
    name: string;
    alias: string | null;
  }>,
): Array<C> =>
  columns?.map((column) => ({
    ...column,
    name: getColumnName(column.name, modelColumns),
  }));

export const aliasRow = (
  row,
  columns: Array<{
    name: string;
    alias: string | null;
    disable_preview: boolean;
    redacted_text: string | null;
    privacy_level?: string | null;
  }>,
) => {
  const keys = Object.keys(row);

  // Add redacted columns back to the row
  const redactedColumns = columns?.filter(isColumnPreviewMasked);
  redactedColumns?.forEach(({ name }) => keys.push(name));

  return keys.reduce((rest, key) => {
    const modelColumn = columns?.find(({ name }) => name === key);
    const alias = modelColumn?.alias;
    const previewDisabled = modelColumn
      ? isColumnPreviewMasked(modelColumn)
      : false;
    const redactedText = modelColumn?.redacted_text;
    const value = row[key];

    const newKey = alias || key;
    const newValue = previewDisabled
      ? (redactedText ?? REDACTED_PREVIEW_VALUE)
      : value;

    return { [newKey]: newValue, ...rest };
  }, {});
};

export const getColumnName = (
  queryColumn: string,
  modelColumns: Array<{ name: string; alias: string | null }>,
): string => {
  const alias = modelColumns?.find(({ name }) => name === queryColumn)?.alias;
  return alias || queryColumn;
};

export const getColumnNameFromAlias = (
  alias: string,
  modelColumns: Array<{ name: string; alias: string | null }>,
): string => {
  const name = modelColumns?.find(({ alias: a }) => a === alias)?.name;
  return name || alias;
};

export const getRawModelRow = (
  row: Record<string, any>,
  modelColumns: Array<{ name: string; alias: string | null }>,
) => {
  return Object.entries(row).reduce((acc, [key, value]) => {
    return { ...acc, [getColumnNameFromAlias(key, modelColumns)]: value };
  }, {});
};

export const isTimestampColumn = (column: {
  type: string | null;
  custom_type?: string | null;
}): boolean => {
  return (
    column.type === ColumnType.Timestamp ||
    column.custom_type === ColumnType.Timestamp
  );
};

export const isStringColumn = (column: {
  type: string | null;
  custom_type?: string | null;
}): boolean => {
  return (
    column.type === ColumnType.String ||
    column.custom_type === ColumnType.String
  );
};

export const isBooleanColumn = (column: {
  type: string | null;
  custom_type?: string | null;
}): boolean => {
  return (
    column.type === ColumnType.Boolean ||
    column.custom_type === ColumnType.Boolean
  );
};

export const isIntegerColumn = (column: {
  type: string | null;
  custom_type?: string | null;
}): boolean => {
  return (
    column.type === ColumnType.Number ||
    column.custom_type === ColumnType.Number
  );
};

export const QueryTypeBadge: FC<
  Readonly<{ type: QueryType; children: BadgeProps["children"] }>
> = ({ type, children }) => {
  switch (type) {
    case QueryType.RawSql:
      return (
        <Badge size="sm" svgIcon={SqlIcon}>
          {children}
        </Badge>
      );
    case QueryType.Visual:
      return (
        <Badge size="sm" svgIcon={AudienceIcon}>
          {children}
        </Badge>
      );
    case QueryType.DbtModel:
    case QueryType.DbtCloudModel:
      return (
        <Badge size="sm" svgIcon={DbtIcon}>
          {children}
        </Badge>
      );
    case QueryType.Table:
      return (
        <Badge size="sm" svgIcon={TableIcon}>
          {children}
        </Badge>
      );
    case QueryType.JourneyNode:
      return (
        <Badge size="sm" svgIcon={JourneyIcon}>
          {children}
        </Badge>
      );
    default:
      return (
        <Badge size="sm" svgIcon={ModelIcon}>
          {children}
        </Badge>
      );
  }
};
